'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import {
  Eye, EyeOff, Mail, Phone, Lock, User, AtSign,
  Gift, AlertCircle, Loader2,
  ShieldCheck, ArrowRight, CheckCircle, CheckCircle2,
} from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { updateUserProfile } from '@/lib/supabaseService';
import { createClient } from '@/lib/supabase/client';
import { trackLogin, trackSignupStarted, trackSignupPaymentInitiated } from '@/lib/analytics';
import UpiPaymentModal from '@/components/UpiPaymentModal';

// Single shared client — never recreated across renders
const supabase = createClient();

type Tab = 'login' | 'register';

interface LoginForm {
  emailOrPhone: string;
  password: string;
}

interface RegisterForm {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  refCode: string;
  instagram: string;
  acceptTerms: boolean;
}

interface ForgotPasswordForm {
  email: string;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1.5 font-medium">
      <AlertCircle size={12} className="flex-shrink-0" />
      {message}
    </p>
  );
}

function FieldSuccess({ show }: { show: boolean }) {
  if (!show) return null;
  return <CheckCircle2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />;
}

export default function AuthClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, signUp, user: authUser, loading: authLoading } = useAuth();
  const [tab, setTab] = useState<Tab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingRegisterData, setPendingRegisterData] = useState<RegisterForm | null>(null);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [submittedUtr, setSubmittedUtr] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Guard: only run the profile redirect check once per unique userId
  const lastRedirectCheckUserId = useRef<string | null>(null);
  // Cache profile fetched during login so the redirect useEffect doesn't re-fetch
  const cachedProfileRef = useRef<{ userId: string; is_admin: boolean; payment_status: string; suspended: boolean } | null>(null);
  // Cooldown: track last login attempt time to prevent rapid retries
  const lastLoginAttemptRef = useRef<number>(0);
  // Track consecutive failed attempts to show helpful guidance
  const failedAttemptsRef = useRef<number>(0);

  const loginForm = useForm<LoginForm>({ mode: 'onChange' });
  const registerForm = useForm<RegisterForm>({ defaultValues: { refCode: '' }, mode: 'onChange' });
  const forgotPasswordForm = useForm<ForgotPasswordForm>({ mode: 'onChange' });

  const watchedEmail = registerForm.watch('email');
  const watchedPhone = registerForm.watch('phone');
  const watchedPassword = registerForm.watch('password');
  const watchedConfirmPassword = registerForm.watch('confirmPassword');
  const watchedLoginInput = loginForm.watch('emailOrPhone');

  const isEmailValid = watchedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(watchedEmail);
  const isPhoneValid = watchedPhone && /^[6-9]\d{9}$/.test(watchedPhone);
  const isPasswordStrong = watchedPassword && watchedPassword.length >= 6;
  const isPasswordMatch = watchedConfirmPassword && watchedPassword === watchedConfirmPassword;
  const isLoginInputValid = watchedLoginInput && (
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(watchedLoginInput) ||
    /^[6-9]\d{9}$/.test(watchedLoginInput.replace(/\s+/g, ''))
  );

  // Redirect already-logged-in users — fires at most once per unique userId
  useEffect(() => {
    if (authLoading || !authUser || isRedirecting) return;
    if (lastRedirectCheckUserId.current === authUser.id) return;

    lastRedirectCheckUserId.current = authUser.id;

    // Reuse profile cached during handleLogin to avoid a duplicate DB call
    if (cachedProfileRef.current && cachedProfileRef.current.userId === authUser.id) {
      const profile = cachedProfileRef.current;
      cachedProfileRef.current = null;
      if (profile && !profile.suspended && (profile.is_admin || profile.payment_status === 'paid')) {
        setIsRedirecting(true);
        router.replace(profile.is_admin ? '/admin?tab=analytics' : '/dashboard');
      }
      return;
    }

    // Only fetch profile if we don't have a cached one (e.g. page refresh while logged in)
    // Use server-side API route to avoid anon rate-limit
    fetch(`/api/auth/profile-check?userId=${encodeURIComponent(authUser.id)}`)
      .then((r) => r.json())
      .then(({ profile }) => {
        if (profile && !profile.suspended && (profile.is_admin || profile.payment_status === 'paid')) {
          setIsRedirecting(true);
          router.replace(profile.is_admin ? '/admin?tab=analytics' : '/dashboard');
        }
      })
      .catch(() => {
        lastRedirectCheckUserId.current = null;
      });
  }, [authUser?.id, authLoading, isRedirecting, router]);

  // Handle ref/tab query params
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      registerForm.setValue('refCode', ref);
      setTab('register');
    }
    const tabParam = searchParams.get('tab');
    if (tabParam === 'register') {
      setTab('register');
    }
  }, [searchParams, registerForm]);

  // ── Login ──────────────────────────────────────────────────────────

  const handleLogin = async (data: LoginForm) => {
    // Cooldown: prevent submitting more than once every 15 seconds to avoid Supabase auth rate limits
    const now = Date.now();
    if (now - lastLoginAttemptRef.current < 15000) {
      const remaining = Math.ceil((15000 - (now - lastLoginAttemptRef.current)) / 1000);
      loginForm.setError('emailOrPhone', {
        message: `Please wait ${remaining} second${remaining !== 1 ? 's' : ''} before trying again.`,
      });
      return;
    }
    lastLoginAttemptRef.current = now;

    setLoginLoading(true);
    try {
      let loginEmail = data.emailOrPhone.trim();
      const isPhone = /^[6-9]\d{9}$/.test(loginEmail.replace(/\s+/g, ''));

      if (isPhone) {
        // Use server-side API route for phone lookup — avoids burning anon rate-limit slots
        const phoneClean = loginEmail.replace(/\s+/g, '');
        try {
          const res = await fetch(`/api/auth/phone-lookup?phone=${encodeURIComponent(phoneClean)}`);
          const json = await res.json();
          if (!json.email) {
            loginForm.setError('emailOrPhone', {
              message: 'No account found with this phone number. Try your email address instead.',
            });
            setLoginLoading(false);
            return;
          }
          loginEmail = json.email;
        } catch {
          loginForm.setError('emailOrPhone', {
            message: 'Could not look up phone number. Please use your email address.',
          });
          setLoginLoading(false);
          return;
        }
      }

      // Sign in directly
      let result: any;
      try {
        result = await signIn(loginEmail, data.password);
      } catch (signInErr: any) {
        const msg = (signInErr?.message || '').toLowerCase();
        failedAttemptsRef.current += 1;

        if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
          loginForm.setError('password', {
            message: 'Email not verified. Check your inbox for a confirmation link.',
          });
        } else if (msg.includes('invalid login') || msg.includes('invalid credentials') || msg.includes('wrong password')) {
          const attemptsMsg = failedAttemptsRef.current >= 3
            ? 'Incorrect password. Too many failed attempts — use "Forgot password?" to reset your password.' :'Incorrect password. Please try again or use "Forgot password?"';
          loginForm.setError('password', { message: attemptsMsg });
        } else if (
          msg.includes('too many requests') ||
          msg.includes('rate limit') ||
          msg.includes('too many attempts') ||
          msg.includes('over_email_send_rate_limit') ||
          msg.includes('email rate limit') ||
          msg.includes('request rate limit')
        ) {
          // Reset cooldown to force a longer wait after a rate limit error
          lastLoginAttemptRef.current = now + 45000; // force 60s total wait (45s extra)
          loginForm.setError('emailOrPhone', {
            message: 'Too many login attempts. Supabase has temporarily blocked sign-ins. Please wait 1–2 minutes before trying again.',
          });
        } else {
          loginForm.setError('password', {
            message: signInErr?.message || 'Login failed. Please check your details and try again.',
          });
        }
        setLoginLoading(false);
        return;
      }

      // Reset failed attempts on success
      failedAttemptsRef.current = 0;

      const loggedInUser = result?.user;
      if (!loggedInUser) {
        loginForm.setError('password', { message: 'Login failed. Please try again.' });
        setLoginLoading(false);
        return;
      }

      // Fetch profile via server-side API route — avoids burning anon rate-limit slots
      let profileRow: { id: string; full_name: string; is_admin: boolean; payment_status: string; suspended: boolean } | null = null;
      try {
        const profileRes = await fetch(`/api/auth/profile-check?userId=${encodeURIComponent(loggedInUser.id)}`);
        const profileJson = await profileRes.json();
        profileRow = profileJson.profile ?? null;
      } catch {
        profileRow = null;
      }

      // Fallback: if API route failed, query directly with the now-authenticated client
      // (authenticated queries don't count against the anon rate limit)
      if (!profileRow) {
        try {
          const { data: fallbackProfile } = await supabase
            .from('user_profiles')
            .select('id, full_name, is_admin, payment_status, suspended')
            .eq('id', loggedInUser.id)
            .maybeSingle();
          profileRow = fallbackProfile ?? null;
        } catch {
          profileRow = null;
        }
      }

      if (!profileRow) {
        await supabase.auth.signOut();
        loginForm.setError('emailOrPhone', {
          message: 'Account not found. Please register or contact support.',
        });
        setLoginLoading(false);
        return;
      }

      if (profileRow.suspended) {
        await supabase.auth.signOut();
        loginForm.setError('emailOrPhone', {
          message: 'Your account has been suspended. Please contact support.',
        });
        setLoginLoading(false);
        return;
      }

      if (!profileRow.is_admin && profileRow.payment_status !== 'paid') {
        await supabase.auth.signOut();
        loginForm.setError('emailOrPhone', {
          message: 'Your account is pending activation. Our team will activate it within 12–24 hours after verifying your payment.',
        });
        setLoginLoading(false);
        return;
      }

      // Cache profile so redirect useEffect reuses it (no second DB call)
      cachedProfileRef.current = {
        userId: loggedInUser.id,
        is_admin: profileRow.is_admin,
        payment_status: profileRow.payment_status,
        suspended: profileRow.suspended,
      };

      trackLogin(loggedInUser.id);
      toast.success(`Welcome back, ${profileRow.full_name?.split(' ')[0] ?? ''}!`);

      const redirectTarget = profileRow.is_admin ? '/admin?tab=analytics' : '/dashboard';
      setIsRedirecting(true);
      router.replace(redirectTarget);
    } catch (err: any) {
      loginForm.setError('password', {
        message: err?.message || 'Login failed. Please try again.',
      });
    }
    setLoginLoading(false);
  };

  // ── Forgot Password ────────────────────────────────────────────────

  const handleForgotPassword = async (data: ForgotPasswordForm) => {
    setForgotPasswordLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/reset-password`,
      });
      if (error) throw error;
      setForgotPasswordSent(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (err: any) {
      forgotPasswordForm.setError('email', {
        message: err?.message || 'Failed to send reset email. Please try again.',
      });
    }
    setForgotPasswordLoading(false);
  };

  // ── Register ───────────────────────────────────────────────────────

  const handleRegisterSubmit = async (data: RegisterForm) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match. Please re-enter.' });
      return;
    }
    trackSignupStarted();
    setRegisterLoading(true);

    try {
      const signUpResult = await signUp(data.email, data.password, {
        fullName: data.fullName,
        phone: data.phone,
        instagram: data.instagram || '',
        referredBy: data.refCode?.toUpperCase() || '',
      });

      const user = signUpResult?.user;

      if (!user) {
        throw new Error('Account creation failed. Please try again.');
      }

      const upiOrderId = `DS-REG-${user.id.slice(0, 8).toUpperCase()}-${Date.now()}`;

      await updateUserProfile(user.id, {
        upiOrderId: upiOrderId,
        paymentStatus: 'unpaid',
      });

      await supabase.auth.signOut();

      setPendingRegisterData({ ...data, _upiOrderId: upiOrderId, _userId: user.id } as any);
      trackSignupPaymentInitiated(4999);
    } catch (err: any) {
      registerForm.setError('fullName', { message: err?.message || 'Registration failed. Please try again.' });
      setRegisterLoading(false);
      return;
    }

    setRegisterLoading(false);
    setShowPaymentModal(true);
  };

  const handleUpiPaymentSubmitted = (utrNumber: string) => {
    setSubmittedUtr(utrNumber);
    setPaymentSubmitted(true);
    setShowPaymentModal(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const inputBase = 'w-full pl-10 pr-4 py-3.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-card transition-colors';
  const inputNormal = `${inputBase} border-input`;
  const inputError = `${inputBase} border-red-400 bg-red-50/30 focus:ring-red-300 focus:border-red-400`;
  const inputSuccess = `${inputBase} border-green-400 focus:ring-green-300 focus:border-green-400`;

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 auth-brand-panel flex-col justify-between p-10 xl:p-14">
        <div className="flex items-center gap-3">
          <span className="text-xl font-bold text-white">
            dropservice<span className="text-blue-400">.in</span>
          </span>
        </div>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
              Your digital services<br />reselling platform.
            </h1>
            <p className="mt-3 text-blue-200 text-base leading-relaxed">
              Place orders, earn profits, grow your team — all from one dashboard built for Indian freelancers.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Active Members', value: '2,400+' },
              { label: 'Orders Placed', value: '18,500+' },
              { label: 'Avg. Profit/Order', value: '₹8,200' },
              { label: 'Cities Covered', value: '340+' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <p className="text-2xl font-bold text-white font-tabular">{stat.value}</p>
                <p className="text-xs text-blue-200 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-blue-300">© 2025 DropService.in · All rights reserved</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-5 sm:p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <span className="text-lg font-bold">
              <span className="text-foreground">dropservice</span>
              <span className="text-primary">.in</span>
            </span>
          </div>

          {/* Forgot Password View */}
          {showForgotPassword ? (
            <div className="animate-fade-in space-y-4">
              <div>
                <h2 className="text-xl font-bold text-foreground">Reset Password</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your email and we&apos;ll send you a reset link.
                </p>
              </div>

              {forgotPasswordSent ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center space-y-2">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Mail size={22} className="text-green-600" />
                  </div>
                  <p className="text-sm font-semibold text-green-800">Reset link sent!</p>
                  <p className="text-xs text-green-700">
                    Check your inbox and follow the link to reset your password.
                  </p>
                </div>
              ) : (
                <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        {...forgotPasswordForm.register('email', { required: 'Please enter your email address' })}
                        placeholder="you@example.com"
                        className={forgotPasswordForm.formState.errors.email ? inputError : inputNormal}
                      />
                    </div>
                    <FieldError message={forgotPasswordForm.formState.errors.email?.message} />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="w-full gradient-primary text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70"
                  >
                    {forgotPasswordLoading ? <Loader2 size={16} className="animate-spin" /> : <>Send Reset Link <ArrowRight size={15} /></>}
                  </button>
                </form>
              )}

              <button
                type="button"
                onClick={() => { setShowForgotPassword(false); setForgotPasswordSent(false); forgotPasswordForm.reset(); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center py-2"
              >
                ← Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <div className="flex rounded-xl bg-muted p-1 mb-6">
                {(['login', 'register'] as Tab[]).map((t) => (
                  <button
                    key={`tab-${t}`}
                    onClick={() => setTab(t)}
                    className={[
                      'flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200',
                      tab === t ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground hover:text-foreground',
                    ].join(' ')}
                  >
                    {t === 'login' ? 'Sign In' : 'Create Account'}
                  </button>
                ))}
              </div>

              {tab === 'login' && (
                <div className="animate-fade-in space-y-4">
                  <div>
                    <h2 className="text-xl font-bold text-foreground">Welcome back</h2>
                    <p className="text-sm text-muted-foreground mt-1">Sign in to your member dashboard</p>
                  </div>
                  <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Email or Phone Number</label>
                      <div className="relative">
                        {watchedLoginInput && /^[6-9]/.test(watchedLoginInput) ? (
                          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        ) : (
                          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        )}
                        <input
                          type="text"
                          inputMode="email"
                          autoComplete="username"
                          {...loginForm.register('emailOrPhone', {
                            required: 'Please enter your email or phone number',
                            validate: (v) => {
                              const clean = v.trim().replace(/\s+/g, '');
                              const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean);
                              const isPhone = /^[6-9]\d{9}$/.test(clean);
                              if (!isEmail && !isPhone) return 'Enter a valid email or 10-digit mobile number';
                              return true;
                            },
                          })}
                          placeholder="Email or 10-digit phone"
                          className={loginForm.formState.errors.emailOrPhone ? inputError : isLoginInputValid ? inputSuccess : inputNormal}
                        />
                        <FieldSuccess show={!!isLoginInputValid && !loginForm.formState.errors.emailOrPhone} />
                      </div>
                      <FieldError message={loginForm.formState.errors.emailOrPhone?.message} />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-sm font-semibold text-foreground">Password</label>
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          {...loginForm.register('password', { required: 'Please enter your password' })}
                          placeholder="Enter your password"
                          className={`${loginForm.formState.errors.password ? inputError : inputNormal} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <FieldError message={loginForm.formState.errors.password?.message} />
                    </div>

                    <button
                      type="submit"
                      disabled={loginLoading}
                      className="w-full gradient-primary text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70 mt-2"
                    >
                      {loginLoading ? <Loader2 size={16} className="animate-spin" /> : <>Sign In <ArrowRight size={15} /></>}
                    </button>
                  </form>
                </div>
              )}

              {tab === 'register' && (
                <div className="animate-fade-in space-y-4">
                  <div>
                    <h2 className="text-xl font-bold text-foreground">Create Account</h2>
                    <p className="text-sm text-muted-foreground mt-1">Join as an authorized dropservice.in agent</p>
                  </div>
                  <form onSubmit={registerForm.handleSubmit(handleRegisterSubmit)} className="space-y-3.5">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Full Legal Name <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          autoComplete="name"
                          {...registerForm.register('fullName', { required: 'Full name is required' })}
                          placeholder="As per Aadhaar card"
                          className={registerForm.formState.errors.fullName ? inputError : inputNormal}
                        />
                      </div>
                      <FieldError message={registerForm.formState.errors.fullName?.message} />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          {...registerForm.register('email', {
                            required: 'Email address is required',
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address (e.g. you@gmail.com)' },
                          })}
                          placeholder="you@example.com"
                          className={`${registerForm.formState.errors.email ? inputError : isEmailValid ? inputSuccess : inputNormal} pr-10`}
                        />
                        <FieldSuccess show={!!isEmailValid && !registerForm.formState.errors.email} />
                      </div>
                      <FieldError message={registerForm.formState.errors.email?.message} />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel"
                          {...registerForm.register('phone', {
                            required: 'Phone number is required',
                            pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number (starts with 6–9)' },
                          })}
                          placeholder="10-digit mobile number"
                          maxLength={10}
                          className={`${registerForm.formState.errors.phone ? inputError : isPhoneValid ? inputSuccess : inputNormal} pr-10`}
                        />
                        <FieldSuccess show={!!isPhoneValid && !registerForm.formState.errors.phone} />
                      </div>
                      <FieldError message={registerForm.formState.errors.phone?.message} />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Password <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...registerForm.register('password', {
                            required: 'Password is required',
                            minLength: { value: 6, message: 'Password must be at least 6 characters' },
                          })}
                          placeholder="Min. 6 characters"
                          className={`${registerForm.formState.errors.password ? inputError : isPasswordStrong ? inputSuccess : inputNormal} pr-10`}
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground p-1">
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <FieldError message={registerForm.formState.errors.password?.message} />
                      {watchedPassword && watchedPassword.length > 0 && watchedPassword.length < 6 && !registerForm.formState.errors.password && (
                        <p className="mt-1 text-xs text-amber-600 flex items-center gap-1.5">
                          <AlertCircle size={11} /> {6 - watchedPassword.length} more character{6 - watchedPassword.length !== 1 ? 's' : ''} needed
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...registerForm.register('confirmPassword', {
                            required: 'Please confirm your password',
                            validate: (v) => v === watchedPassword || 'Passwords do not match',
                          })}
                          placeholder="Re-enter password"
                          className={`${registerForm.formState.errors.confirmPassword ? inputError : isPasswordMatch ? inputSuccess : inputNormal} pr-10`}
                        />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground p-1">
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <FieldError message={registerForm.formState.errors.confirmPassword?.message} />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Referral Code <span className="text-muted-foreground font-normal text-xs">(optional)</span></label>
                      <div className="relative">
                        <Gift size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          {...registerForm.register('refCode')}
                          placeholder="Enter referral code (optional)"
                          className={`${inputNormal} uppercase`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-1.5">Instagram Handle <span className="text-muted-foreground font-normal text-xs">(optional)</span></label>
                      <div className="relative">
                        <AtSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          {...registerForm.register('instagram')}
                          placeholder="@yourhandle"
                          className={inputNormal}
                        />
                      </div>
                    </div>

                    <div className="flex items-start gap-3 pt-1">
                      <input
                        type="checkbox"
                        id="acceptTerms"
                        {...registerForm.register('acceptTerms', { required: 'You must accept the terms to continue' })}
                        className="mt-0.5 w-5 h-5 accent-primary flex-shrink-0 cursor-pointer"
                      />
                      <label htmlFor="acceptTerms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                        I have read and agree to the{' '}
                        <Link href="/terms" target="_blank" className="text-primary font-semibold hover:underline">Terms of Service</Link>
                        {' '}and{' '}
                        <Link href="/privacy-policy" target="_blank" className="text-primary font-semibold hover:underline">Privacy Policy</Link>
                        {'. '}I understand that wallet funds are non-withdrawable and the registration fee is non-refundable once my account is activated.
                      </label>
                    </div>
                    <FieldError message={registerForm.formState.errors.acceptTerms?.message} />

                    <button
                      type="submit"
                      disabled={registerLoading}
                      className="w-full gradient-primary text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70 mt-1"
                    >
                      {registerLoading ? <Loader2 size={16} className="animate-spin" /> : <>Proceed to Payment <ArrowRight size={15} /></>}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* UPI Payment Modal */}
      {showPaymentModal && pendingRegisterData && (
        <UpiPaymentModal
          amount={4999}
          purpose="registration"
          orderId={(pendingRegisterData as any)._upiOrderId || `DS-REG-${Date.now()}`}
          userId={(pendingRegisterData as any)._userId}
          onClose={() => { setShowPaymentModal(false); }}
          onSubmitted={handleUpiPaymentSubmitted}
        />
      )}

      {/* Payment Submitted Confirmation */}
      {paymentSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-slide-up text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Payment Reference Submitted!</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Your UTR <strong className="text-foreground">{submittedUtr}</strong> has been submitted for verification.
              </p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-left">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                ⏳ <strong>Account activation takes up to 24 hours.</strong> Once admin verifies your payment, you can log in to your dashboard.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
              <ShieldCheck size={13} className="text-primary flex-shrink-0" />
              <span>Contact support if not activated within 24 hours.</span>
            </div>
            <button
              onClick={() => { setPaymentSubmitted(false); setTab('login'); }}
              className="w-full py-3 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      )}
    </div>
  );
}